import { useRef, type ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useReactFlow, type Viewport } from '@xyflow/react'
import { useFlowStore } from '../store/flowStore'
import { layoutGraph, type LayoutDirection } from '../layout/autoLayout'
import { exportToXml, importFromXml } from '../xml/customXml'
import { exportToDrawioXml, importFromDrawioXml } from '../xml/drawioXml'
import { exportToJson, importFromJson } from '../json/flowJson'
import { exportToPng } from '../utils/exportImage'
import { downloadFile, slugify } from '../utils/download'
import { ToolbarMenu } from './ToolbarMenu'
import type { QualityFlowchartStatus } from '../../../types/qualityFlowchart'

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

type ImportFormat = 'xml' | 'json' | 'drawio'

const IMPORT_ACCEPT: Record<ImportFormat, string> = {
  xml: '.xml,text/xml,application/xml',
  json: '.json,application/json',
  drawio: '.drawio,.xml,text/xml,application/xml',
}

type FlowToolbarProps = {
  saveStatus?: SaveStatus
  canEdit?: boolean
  status?: QualityFlowchartStatus
  dirty?: boolean
  onBack?: () => void
  onSave?: () => void
  onPublish?: () => void
  publishing?: boolean
  /** Mostra "Criar chat" na toolbar (somente se ainda não houver chat). */
  showCreateAiChat?: boolean
  creatingAiChat?: boolean
  onCreateAiChat?: () => void
}

export function FlowToolbar({
  saveStatus = 'idle',
  canEdit = true,
  status = 'draft',
  dirty = false,
  onBack,
  onSave,
  onPublish,
  publishing = false,
  showCreateAiChat = false,
  creatingAiChat = false,
  onCreateAiChat,
}: FlowToolbarProps) {
  const navigate = useNavigate()
  const flowName = useFlowStore((s) => s.flowName)
  const setFlowName = useFlowStore((s) => s.setFlowName)
  const presentationMode = useFlowStore((s) => s.presentationMode)
  const togglePresentationMode = useFlowStore((s) => s.togglePresentationMode)
  const readOnly = useFlowStore((s) => s.readOnly)
  const undo = useFlowStore((s) => s.undo)
  const redo = useFlowStore((s) => s.redo)
  const canUndo = useFlowStore((s) => s.past.length > 0)
  const canRedo = useFlowStore((s) => s.future.length > 0)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const importFormatRef = useRef<ImportFormat>('xml')
  const { fitView, setViewport } = useReactFlow()

  const handleBack = () => {
    if (onBack) onBack()
    else navigate('/qualidade/fluxograma')
  }

  const applyLayout = (direction: LayoutDirection) => {
    const { nodes, edges, setGraph } = useFlowStore.getState()
    setGraph(layoutGraph(nodes, edges, direction), edges)
    window.requestAnimationFrame(() => fitView({ padding: 0.15, duration: 300 }))
  }

  const handleExportXml = () => {
    const { nodes, edges, viewport, flowName: name } = useFlowStore.getState()
    downloadFile(
      exportToXml({ nodes, edges, viewport, name }),
      `${slugify(name)}.xml`,
    )
  }

  const handleExportDrawio = () => {
    const { nodes, edges, flowName: name } = useFlowStore.getState()
    downloadFile(
      exportToDrawioXml(nodes, edges, name),
      `${slugify(name)}.drawio.xml`,
    )
  }

  const handleExportJson = () => {
    const { nodes, edges, viewport, edgeType, flowName: name } =
      useFlowStore.getState()
    downloadFile(
      exportToJson({ nodes, edges, viewport, edgeType, name }),
      `${slugify(name)}.json`,
      'application/json',
    )
  }

  const handleExportPng = async () => {
    const { nodes, flowName: name } = useFlowStore.getState()
    try {
      await exportToPng(nodes, `${slugify(name)}.png`)
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Falha ao exportar PNG.')
    }
  }

  const openImportPicker = (format: ImportFormat) => {
    importFormatRef.current = format
    const input = fileInputRef.current
    if (!input) return
    input.accept = IMPORT_ACCEPT[format]
    input.click()
  }

  const applyImportedViewport = (viewport: Viewport | null) => {
    if (viewport) {
      useFlowStore.getState().setViewportState(viewport)
      void setViewport(viewport, { duration: 300 })
    } else {
      window.requestAnimationFrame(() =>
        fitView({ padding: 0.15, duration: 300 }),
      )
    }
  }

  const handleImportFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    try {
      const text = await file.text()
      const store = useFlowStore.getState()
      const format = importFormatRef.current

      if (format === 'json') {
        const doc = importFromJson(text)
        store.setGraph(doc.nodes, doc.edges)
        store.setFlowName(doc.name)
        store.setEdgeType(doc.edgeType)
        applyImportedViewport(doc.viewport)
      } else if (format === 'drawio') {
        const doc = importFromDrawioXml(text)
        store.setGraph(doc.nodes, doc.edges)
        store.setFlowName(doc.name)
        applyImportedViewport(null)
      } else {
        const doc = importFromXml(text)
        store.setGraph(doc.nodes, doc.edges)
        store.setFlowName(doc.name)
        applyImportedViewport(doc.viewport)
      }
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : 'Não foi possível importar o arquivo.',
      )
    }
  }

  const handleClear = () => {
    if (window.confirm('Remover todos os nós e conexões do fluxograma?')) {
      useFlowStore.getState().clearAll()
    }
  }

  const statusBadge =
    status === 'published' ? (
      <span className="toolbar-chip" title="Fluxograma publicado">
        Publicado
      </span>
    ) : (
      <span className="toolbar-chip" title="Rascunho — salve manualmente">
        Rascunho
      </span>
    )

  const saveLabel =
    saveStatus === 'saving'
      ? 'Salvando…'
      : saveStatus === 'saved'
        ? 'Salvo'
        : saveStatus === 'error'
          ? 'Erro'
          : dirty
            ? 'Não salvas'
            : ''

  if (presentationMode || readOnly) {
    return (
      <header className="toolbar presentation-toolbar">
        <div className="toolbar-group brand">
          <button type="button" onClick={handleBack} title="Voltar à lista">
            ← Voltar
          </button>
          <span className="flow-name-static" title="Nome do fluxograma">
            {flowName}
          </span>
          {statusBadge}
          {readOnly && (
            <span className="toolbar-chip" title="Somente visualização">
              Leitura
            </span>
          )}
        </div>

        <div className="toolbar-group toolbar-right">
          {saveLabel && <span className="toolbar-label">{saveLabel}</span>}
          <button
            type="button"
            onClick={() => fitView({ padding: 0.15, duration: 300 })}
            title="Enquadrar o fluxograma na tela"
          >
            ⤢
          </button>
          <button type="button" onClick={handleExportPng} title="Exportar PNG">
            PNG
          </button>
          {!readOnly && (
            <button
              type="button"
              className="primary"
              onClick={togglePresentationMode}
              title="Voltar para o modo de edição"
            >
              ✎ Editar
            </button>
          )}
        </div>
      </header>
    )
  }

  return (
    <header className="toolbar">
      <div className="toolbar-group brand">
        <button type="button" onClick={handleBack} title="Voltar à lista">
          ← Voltar
        </button>
        <input
          className="flow-name-input"
          value={flowName}
          onChange={(e) => setFlowName(e.target.value)}
          placeholder="Nome do fluxograma"
          title="Nome do fluxograma"
          disabled={!canEdit}
        />
        {statusBadge}
        {saveLabel && <span className="toolbar-label">{saveLabel}</span>}
      </div>

      <div className="toolbar-group">
        <button
          type="button"
          className="toolbar-icon-btn"
          onClick={undo}
          disabled={!canUndo || !canEdit}
          title="Desfazer (Ctrl+Z)"
          aria-label="Desfazer"
        >
          ↩
        </button>
        <button
          type="button"
          className="toolbar-icon-btn"
          onClick={redo}
          disabled={!canRedo || !canEdit}
          title="Refazer (Ctrl+Y)"
          aria-label="Refazer"
        >
          ↪
        </button>
        <span className="toolbar-sep" />
        <button
          type="button"
          onClick={() => applyLayout('LR')}
          disabled={!canEdit}
          title="Organizar layout na horizontal"
        >
          → Horizontal
        </button>
        <button
          type="button"
          onClick={() => applyLayout('TB')}
          disabled={!canEdit}
          title="Organizar layout na vertical"
        >
          ↓ Vertical
        </button>
      </div>

      <div className="toolbar-group toolbar-right">
        {canEdit && onSave && (
          <button
            type="button"
            className={status === 'published' || !onPublish ? 'primary' : undefined}
            onClick={onSave}
            disabled={!dirty || saveStatus === 'saving' || publishing}
            title={
              status === 'draft'
                ? 'Salvar rascunho (sem publicar)'
                : 'Salvar e criar nova versão'
            }
          >
            {saveStatus === 'saving' ? 'Salvando…' : 'Salvar'}
          </button>
        )}
        {status === 'draft' && canEdit && onPublish && (
          <button
            type="button"
            className="primary"
            onClick={onPublish}
            disabled={publishing || saveStatus === 'saving'}
            title="Publicar e iniciar histórico de versões"
          >
            {publishing ? 'Publicando…' : 'Publicar'}
          </button>
        )}
        {showCreateAiChat && canEdit && onCreateAiChat && (
          <button
            type="button"
            className="primary"
            onClick={onCreateAiChat}
            disabled={creatingAiChat}
            title="Abrir assistente de IA para criar ou editar este fluxograma"
          >
            {creatingAiChat ? 'Abrindo…' : 'Assistente IA'}
          </button>
        )}
        <ToolbarMenu
          label="↓ Exportar"
          title="Exportar fluxograma"
          items={[
            { key: 'xml', label: 'XML', onSelect: handleExportXml },
            { key: 'json', label: 'JSON', onSelect: handleExportJson },
            { key: 'png', label: 'PNG', onSelect: () => void handleExportPng() },
            { key: 'drawio', label: 'draw.io', onSelect: handleExportDrawio },
          ]}
        />
        <ToolbarMenu
          label="↑ Importar"
          title="Importar fluxograma"
          disabled={!canEdit}
          items={[
            { key: 'xml', label: 'XML', onSelect: () => openImportPicker('xml') },
            { key: 'json', label: 'JSON', onSelect: () => openImportPicker('json') },
            {
              key: 'drawio',
              label: 'draw.io',
              onSelect: () => openImportPicker('drawio'),
            },
          ]}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".xml,.drawio,text/xml,application/xml"
          hidden
          onChange={handleImportFile}
        />
        {status === 'published' && (
          <button
            type="button"
            className="primary"
            onClick={togglePresentationMode}
            title="Modo apresentação"
          >
            ▶ Apresentar
          </button>
        )}
        <button
          type="button"
          className="danger"
          onClick={handleClear}
          disabled={!canEdit}
          title="Limpar tudo"
        >
          🗑 Limpar
        </button>
      </div>
    </header>
  )
}
